/**
 * Referral performance.
 *
 * The number that decides whether the programme is working is the CONVERSION
 * RATE - attributed signups that went on to a qualifying order. A referral
 * count on its own measures how many codes were typed, which is the metric a
 * fraud farm optimises.
 *
 * "Rewards issued" is read from the LEDGER, not from the referral rows' intended
 * amounts, so it reports money that actually left the business.
 */
import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'

import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import { Card, CardTitle, PillButton, StatCard, StatRow, TableHead, Td } from '../../features/b2b/components/primitives'
import { useColors } from '../../hooks/useColors'
import { formatINR, getReferralOverview } from '../../services/walletApi'
import type { DateRangeFilter, ReferralOverview } from '../../services/walletApi'

export default function ReferralsTab({ filter }: { filter: DateRangeFilter }) {
  const colors = useColors()
  const [overview, setOverview] = useState<ReferralOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setOverview(await getReferralOverview(filter))
    } catch {
      setError('Could not load referral statistics.')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    void load()
  }, [load])

  if (error) return <LoadErrorState message={error} onRetry={load} />
  if (!overview) return <Card><span style={{ color: colors.textMuted }}>Loading…</span></Card>

  return (
    <>
      <StatRow>
        <StatCard
          label="Referrals attributed"
          value={String(overview.total_referrals)}
          hint="Signups that carried a code"
        />
        <StatCard
          label="Converted"
          value={String(overview.successful_referrals)}
          color="#15803D"
          hint="Friend completed a qualifying order"
        />
        <StatCard
          label="Conversion rate"
          value={`${overview.conversion_rate}%`}
          hint="The number that says whether this buys customers or signups"
        />
        <StatCard
          label="Rewards issued"
          value={formatINR(overview.rewards_issued)}
          color="#B45309"
          hint={`${overview.payout_count} payouts, read from the ledger`}
        />
      </StatRow>

      <StatRow>
        <StatCard
          label="Awaiting first order"
          value={String(overview.pending_referrals)}
          hint="Attributed, nothing owed yet"
        />
        <StatCard
          label="Blocked by fraud checks"
          value={String(overview.rejected_referrals)}
          hint="Self-referrals, duplicate devices, expired campaigns"
        />
        <StatCard
          label="Reversed"
          value={String(overview.reversed_referrals)}
          hint="Reward clawed back after a cancelled order"
        />
      </StatRow>

      <Card padded={false}>
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px', borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <CardTitle>Top referrers</CardTitle>
          <PillButton variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </PillButton>
        </div>

        {overview.top_referrers.length === 0 ? (
          <div style={{ padding: '20px' }}>
            <EmptyState title="No referrals yet" description="Nobody shared a code in this period." />
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <TableHead
                columns={[
                  { key: 'customer', label: 'Customer' },
                  { key: 'code', label: 'Code' },
                  { key: 'referrals', label: 'Referrals', align: 'right' },
                  { key: 'converted', label: 'Converted', align: 'right' },
                  { key: 'earned', label: 'Earned', align: 'right' },
                ]}
              />
              <tbody>
                {overview.top_referrers.map((referrer) => (
                  <tr key={referrer.id}>
                    <Td>
                      <div>{referrer.name || '-'}</div>
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>{referrer.phone}</div>
                    </Td>
                    <Td muted>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {referrer.referral_code || '-'}
                      </span>
                    </Td>
                    <Td align="right">{referrer.referrals}</Td>
                    <Td align="right" strong>{referrer.successful}</Td>
                    <Td align="right" strong style={{ color: '#15803D' }}>{formatINR(referrer.earned)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  )
}
